import { useSelector } from "react-redux";
import client from '../../../lib/feathers';
import PrimaryButton from "../../PrimaryButton";
import { useNavigate } from "react-router-dom";

const TIER_LABELS = {
  0: 'Tier 1 - Claimed',
  98: 'Tier 2 - Enhanced',
  111: 'Tier 3 - Premium',
  127: 'Tier 4 - Reserve',
};

function getTierLabel(featureLevel) {
  return TIER_LABELS[featureLevel] || `Custom (${featureLevel})`;
}

export default function BusinessInfoPanel(props) {
  const listing = useSelector((s) => s.listings.selected);
  const user = useSelector((s) => s.auth.user);
  const navigate = useNavigate();
  const isAdmin = user.role === 'admin';

  const onDeleteListingClick = async () => {
    const resp = confirm('Are you sure you want to delete this listing?'); // eslint-disable-line no-restricted-globals
    if (resp) {
      const service = client.service(`${user.role}/listing`);
      await service.remove(listing.id);
      navigate('/listings');
    }
  };
  return (
    <div className="bg-white shadow overflow-hidden px-4 py-5 sm:rounded-lg sm:p-6">

      <div className="px-4 py-5 sm:px-6 flex justify-between">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Business Information
        </h3>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <PrimaryButton onClick={() => navigate(`/${user.role}/listings/${listing.id}/edit`)}>
            Edit Listing
          </PrimaryButton>
        </div>
      </div>
      <div className="md:flex">
        <div className="md:w-1/2">
          <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
            <dl className="sm:divide-y sm:divide-gray-200">
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Name
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {listing.name}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Category
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {(listing.categories || []).map((c) => {
                    return (
                      <span key={c.id} className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-green-800">
                        {c.name}
                      </span>
                    )
                  })}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Status
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 capitalize">
                  {listing.status}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Short Description
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {listing.shortDescription || 'N/A'}
                </dd>
              </div>
              {listing.hostListing && (
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Host Venue
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <button
                      type="button"
                      className="text-primary hover:text-red-900 font-medium"
                      onClick={() => navigate(`/${user.role}/listings/${listing.hostListing.id}/`)}
                    >
                      {listing.hostListing.name}
                    </button>
                  </dd>
                </div>
              )}
              {listing.childListings && listing.childListings.length > 0 && (
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Hosted Listings
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <div className="space-y-1">
                      {listing.childListings.map((child) => (
                        <button
                          key={child.id}
                          type="button"
                          className="block text-primary hover:text-red-900 font-medium"
                          onClick={() => navigate(`/${user.role}/listings/${child.id}/`)}
                        >
                          {child.name}
                        </button>
                      ))}
                    </div>
                  </dd>
                </div>
              )}
              {isAdmin && (
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Feature Level
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {getTierLabel(listing.featureLevel)}
                  </dd>
                </div>
              )}
              {isAdmin && (
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Hide from Map
                  </dt>
                  <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${listing.hideFromMap ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                      {listing.hideFromMap ? 'Yes' : 'No'}
                    </span>
                  </dd>
                </div>
              )}
              {isAdmin && (
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Excluded
                  </dt>
                  <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${listing.isExcluded ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                      {listing.isExcluded ? 'Yes' : 'No'}
                    </span>
                  </dd>
                </div>
              )}
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Venue ID
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {listing.venueId || 'N/A'}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Logo
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {listing.logoAttachment?.path ? (
                    <img src={`${process.env.REACT_APP_ATTACHMENT_URL}/${listing.logoAttachment.path}?h=80&w=80`} alt="Logo" className="h-20 w-20 rounded-full object-cover" />
                  ) : (
                    <span className="text-gray-400">Not set</span>
                  )}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Cover Photo
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {listing.profileAttachment?.path ? (
                    <img src={`${process.env.REACT_APP_ATTACHMENT_URL}/${listing.profileAttachment.path}?w=300&f=inside`} alt="Cover" className="max-w-[300px] rounded" />
                  ) : (
                    <span className="text-gray-400">Not set</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </div>
        <div className="md:w-1/2 flex justify-center">
          {/* VIP Status Card */}
          {listing.vipNetworkEnabled ? (
            <div className="border-t border-gray-200 px-4 py-5 sm:p-6 w-full">
              <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-lg p-6 border border-primary">
                <div className="flex items-center justify-center mb-4">
                  <div className="h-16 w-16 rounded-full bg-primary flex items-center justify-center">
                    <svg className="h-10 w-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>
                </div>
                <h4 className="text-center text-lg font-semibold text-primary mb-2">VIP Network Member</h4>
                <dl className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <dt className="text-gray-600">Status:</dt>
                    <dd className="font-medium text-green-700">Active</dd>
                  </div>
                  {listing.vipJoinedDate && (
                    <div className="flex justify-between text-sm">
                      <dt className="text-gray-600">Joined:</dt>
                      <dd className="font-medium text-gray-900">
                        {new Date(listing.vipJoinedDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </dd>
                    </div>
                  )}
                  {listing.vipAgreementAcceptedByUser && (
                    <div className="text-sm border-t border-red-200 pt-2 mt-2">
                      <dt className="text-gray-600 mb-1">Accepted By:</dt>
                      <dd className="font-medium text-gray-900">
                        {listing.vipAgreementAcceptedByUser.firstName} {listing.vipAgreementAcceptedByUser.lastName}
                      </dd>
                      <dd className="text-xs text-gray-500">
                        {listing.vipAgreementAcceptedByUser.email}
                      </dd>
                    </div>
                  )}
                </dl>
                <div className="mt-4 text-center">
                  <button
                    onClick={() => navigate(`/${user.role}/listings/${listing.id}/vip`)}
                    className="text-sm text-primary hover:text-red-900 font-medium"
                  >
                    View VIP Details →
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="border-t border-gray-200 px-4 py-5 sm:p-6 w-full">
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <div className="flex items-center justify-center mb-4">
                  <div className="h-16 w-16 rounded-full bg-gray-300 flex items-center justify-center">
                    <svg className="h-10 w-10 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>
                </div>
                <h4 className="text-center text-lg font-semibold text-gray-700 mb-2">Not Enrolled in VIP Network</h4>
                <p className="text-center text-sm text-gray-500">
                  This listing is not currently part of the VIP Network.
                </p>
                <div className="mt-4 text-center">
                  <button
                    onClick={() => navigate(`/${user.role}/listings/${listing.id}/vip`)}
                    className="text-sm text-primary hover:text-red-900 font-medium"
                  >
                    Learn More →
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {isAdmin && (
        <div className="py-5 border-t border-gray-200 sm:flex sm:items-center sm:justify-between">
          <div className="mt-3 sm:mt-0 sm:ml-4">
            <button onClick={onDeleteListingClick} type="button" className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
              Delete this Listing
            </button>
          </div>
        </div>
      )}
    </div>
  );
}